import { useState } from "react";
import { Card } from "../components/ui/card";
import { PageHeader } from "../components/AppLayout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { AGENDA, AGENTES } from "../data/mockData";
import { diasSemana, format } from "../lib/format";
import { es } from "date-fns/locale";

const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const HORAS = Array.from({ length: 11 }, (_, i) => i + 8); // 8 a 18

export default function Agenda() {
  const [eventos, setEventos] = useState(AGENDA);
  const [agente, setAgente] = useState("todos");
  const [tipo, setTipo] = useState("todos");
  const [drag, setDrag] = useState(null);

  const dias = diasSemana();
  const visibles = eventos.filter(
    (e) => (agente === "todos" || e.agenteId === agente) && (tipo === "todos" || e.tipo === tipo)
  );

  const onDrop = (dia, hora) => {
    if (!drag) return;
    setEventos((evs) => evs.map((e) => (e.id === drag ? { ...e, diaSemana: dia, hora } : e)));
    setDrag(null);
    toast.success("Evento movido. Se avisa al agente y a los involucrados.");
  };

  return (
    <div>
      <PageHeader
        titulo="Agenda semanal"
        descripcion="Vista semana por agente: visitas, firmas, vencimientos y tareas. Arrastrá un evento para moverlo de día u horario."
      >
        <Select value={agente} onValueChange={setAgente}>
          <SelectTrigger data-testid="schedule-agent-filter" className="w-[180px] rounded-md">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los agentes</SelectItem>
            {AGENTES.map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger data-testid="schedule-type-filter" className="w-[150px] rounded-md">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            <SelectItem value="Visita">Visitas</SelectItem>
            <SelectItem value="Firma">Firmas</SelectItem>
            <SelectItem value="Vencimiento">Vencimientos</SelectItem>
            <SelectItem value="Tarea">Tareas</SelectItem>
          </SelectContent>
        </Select>
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        {[
          { t: "Visita", c: "#1E3A8A" },
          { t: "Firma", c: "#064E3B" },
          { t: "Vencimiento", c: "#991B1B" },
          { t: "Tarea", c: "#854D0E" },
        ].map((l) => (
          <span key={l.t} className="flex items-center gap-1.5 text-slate-600">
            <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: l.c }} /> {l.t}
          </span>
        ))}
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm overflow-x-auto" data-testid="schedule-calendar-grid">
        <div className="min-w-[860px]">
          <div className="grid grid-cols-8 border-b border-slate-200 bg-slate-50">
            <div className="px-2 py-3 text-xs font-semibold text-slate-400" />
            {dias.map((d, i) => (
              <div key={i} className="px-2 py-3 text-center">
                <p className="text-xs font-semibold text-slate-700">{DIAS[i]}</p>
                <p className="text-[11px] text-slate-400">{format(d, "dd/MM", { locale: es })}</p>
              </div>
            ))}
          </div>
          {HORAS.map((h) => (
            <div key={h} className="grid grid-cols-8 border-b border-slate-100 min-h-[64px]">
              <div className="px-2 py-2 text-[11px] text-slate-400 border-r border-slate-100">{h}:00</div>
              {DIAS.map((_, di) => {
                const cell = visibles.filter((e) => e.diaSemana === di && e.hora === h);
                return (
                  <div
                    key={di}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(di, h)}
                    className="border-r border-slate-100 p-1 space-y-1"
                  >
                    {cell.map((e) => (
                      <div
                        key={e.id}
                        draggable
                        onDragStart={() => setDrag(e.id)}
                        data-testid={`schedule-event-${e.id}`}
                        className="cursor-grab active:cursor-grabbing rounded-md px-2 py-1 text-[11px] font-medium text-white leading-tight"
                        style={{ backgroundColor: e.color }}
                        title={`${e.titulo} — ${e.agenteNombre}`}
                      >
                        <p className="truncate">{e.titulo}</p>
                        <p className="truncate text-white/80">{e.agenteNombre.split(" ")[0]}</p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
